import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Info } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type FC } from 'react';

export type SourceDescriptionPopoverProps = {
  title: string;
  description: string;
};

export const SourceDescriptionPopover: FC<SourceDescriptionPopoverProps> = ({
  title,
  description,
}) => {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const handleEnter = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  // Split on real newlines and literal \n sequences from JSON
  const paragraphs = useMemo(() => {
    return description
      .replace(/\\r\\n|\\r|\\n/g, '\n')
      .split(/\r?\n/)
      .filter(line => line.trim().length > 0);
  }, [description]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="shrink-0 cursor-pointer outline-none focus:outline-none focus-visible:outline-none"
          onMouseEnter={handleEnter}
          onMouseLeave={scheduleClose}
          onClick={e => e.stopPropagation()}
        >
          <Info className="size-3.5 text-neutral-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[360px] max-h-[300px] overflow-y-auto p-4"
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleClose}
        onClick={e => e.stopPropagation()}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-neutral-900">{title}</p>
          {paragraphs.map((paragraph, i) => (
            <p key={i} className="text-sm text-neutral-700 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
